// Player details interface

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useFirebase } from './FirebaseContextProvider';
import TeamsList from './TeamsList';
import TournamentsList from './TournamentsList';
import UserCardTemplate from './UserCardTemplate';
import PlayerCardEdit from './PlayerCardEdit';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import InviteButton from './InviteButton';
import VerifyAgeButton from './VerifyAgeButton';
import PlayerStatus from './PlayerStatus';
import GuidedTour from './GuidedTour';
import { capFirstLetter } from '../utils/misc';
import { UsersRoles, ParentStatuses } from '../utils/enums';
import { useWaiverStatuses, useEntities } from '../utils/hooks';
import placeholderPerson from '../assets/images/placeholder-person.png';
import './PlayerCard.scss';

const steps = [
  {
    target: '.tabs-header-item-tournaments',
    title: 'Tournaments',
    content: 'See all tournaments your teams are admitted to here',
  },
  {
    target: '.tabs-header-item-teams',
    title: 'Your teams',
    content: 'Here is the list of teams you are rostered in',
  },
  {
    target: '.tabs-header-item-profile',
    title: 'Your profile',
    content:
      'Your profile lives here — edit your info and upload a document for age verification',
  },
];
export default function PlayerCard({ player }) {
  const [run, setRun] = useState(false);
  const {
    user,
    userIsAdmin,
    userIsPlayer,
    userIsCoach,
    parentsByPlayerId,
    usersById,
  } = useFirebase();

  const { id, name, teams, birthDate, gender, card, phone, email, verified } =
    player;

  const isNew = id === 'new';

  const { waiverMap } = useWaiverStatuses({
    playerId: isNew ? undefined : id,
  });
  const waiverSigned = waiverMap.get(id);

  const isTheCoach =
    userIsCoach && !!teams?.some((t) => user.teams?.includes(t));

  const isTheUser = user.id === id;

  const { expireDate: cardExp, number: cardNum } = card ?? {};

  const approvedParents = useMemo(
    () =>
      (parentsByPlayerId.get(id) ?? []).filter(
        (e) => e.status === ParentStatuses.APPROVED,
      ),
    [parentsByPlayerId, id],
  );

  // The link names the parent; their document is read by id, since there is no
  // organization-wide users list to look the name up in any more
  const parentIds = useMemo(
    () => approvedParents.map((e) => e.id),
    [approvedParents],
  );
  useEntities('users', parentIds);

  const isTheParent = approvedParents.some((e) => e.id === user.id);

  useEffect(() => {
    if (user.name) setRun(true);
  }, [user.name]);

  return (
    <GuidedTour
      steps={userIsPlayer ? steps : []}
      run={run}
      tourKey='player-card-v3'
    >
      <UserCardTemplate
        header='Player'
        className='player-card'
        backBtn={!isTheUser}
        isTheUser={isTheUser}
        imgPath={player.imgPath}
        imgThumbPath={player.imgThumbPath}
        imgTitle={name}
        name={name}
        placeholder={placeholderPerson}
        details={
          <div className='props-checklist-wrap'>
            <PropItemsBlock>
              <div className='parent-wrap'>
                <p className='gray-600 font-medium'>
                  {approvedParents.length > 1 ? 'Parents: ' : 'Parent: '}
                </p>
                {approvedParents.length > 0 ? (
                  approvedParents.map((entry, i) => {
                    const parentUser = usersById.get(entry.id);
                    return (
                      <span key={entry.id}>
                        {i > 0 && ', '}
                        <Link to={'/parents/' + entry.id} className='gray-1000'>
                          {parentUser?.name ?? 'Parent'}
                        </Link>
                      </span>
                    );
                  })
                ) : (
                  <p className='gray-400'>No approved parents</p>
                )}
              </div>
              <div className='birthdate-wrap'>
                <p className='gray-600 font-medium'>Birth date: </p>
                <p>{birthDate ? birthDate.format('MMM D, YY') : ''}</p>
              </div>
              <PropItem prop='Gender' value={capFirstLetter(gender)} />
              <PropItem prop='Phone' value={phone} />
              <PropItem prop='e-mail' value={email} />
              <PropItem
                prop='Card'
                value={
                  cardNum && cardExp
                    ? `#${cardNum} exp.${cardExp.format('MMM D, YY')}`
                    : 'No card'
                }
              />
              <div className='buttons'>
                {!verified && (userIsAdmin || isTheUser) && (
                  <VerifyAgeButton userId={id} />
                )}
                {(userIsAdmin || isTheUser) && (
                  <InviteButton
                    tokenCreatePayload={{ role: UsersRoles.PARENT, user: id }}
                    invited='parent'
                    showQR
                  >
                    Invite parent
                  </InviteButton>
                )}
              </div>
            </PropItemsBlock>
            <div className='doc-block'>
              <PlayerStatus
                verified={verified ?? false}
                waiverSigned={waiverSigned ?? false}
              />
            </div>
          </div>
        }
        editForm={
          (userIsAdmin || isTheCoach || isTheUser || isTheParent) && (
            <PlayerCardEdit player={player} />
          )
        }
        tabs={[
          {
            title: 'Tournaments',
            hash: '#tournaments',
            component: <TournamentsList userId={id} />,
          },
          {
            title: 'Teams',
            hash: '#teams',
            component: <TeamsList userId={id} />,
          },
        ]}
      />
    </GuidedTour>
  );
}
